<template>
  <header data-component="GrowArticleHeader" class="pt-6 pb-12 lg:pt-12 lg:pb-20">
    <Container>
      <Grid class="items-stretch gap-y-8 lg:gap-y-0 lg:gap-x-10">
        <!-- Image -->
        <div class="col-span-12 lg:col-span-6 flex">
          <div class="overflow-hidden rounded-3xl shadow-sm w-full aspect-square">
            <Image
              :image="image"
              fit="cover"
              class="w-full h-full"
            />
          </div>
        </div>

        <!-- Text content -->
        <div class="col-span-12 lg:col-span-6 flex flex-col justify-center gap-4 lg:gap-6">
          <ul v-if="tags?.length" class="flex flex-wrap gap-2 justify-center lg:justify-start">
            <li v-for="(tag, i) in tags" :key="`${tag.url ?? tag.title}-${i}`" class="min-w-0 max-w-full">
              <GrowArticleTag :title="tag.title" :url="tag.url" />
            </li>
          </ul>

          <Typography
            as="h1"
            variant="header-2"
            class="text-primary-100 grow-article-title text-center lg:text-left">
            {{ title }}
          </Typography>

          <Typography
            as="p"
            variant="main-article"
            class="text-primary-80 grow-article-body">
            {{ description }}
          </Typography>

          <div v-if="author" class="mt-4 flex items-center gap-3">
            <div class="shrink-0 overflow-hidden rounded-full w-12 h-12">
              <Image
                v-if="author.profileImage"
                :image="author.profileImage"
                fit="cover"
                class="w-full h-full"
              />
            </div>

            <div class="flex flex-col">
              <Typography
                as="span"
                variant="main-text-condensed-lh"
                class="text-primary-100 grow-article-author-name">
                {{ author.name }}
              </Typography>
              <Typography
                as="span"
                variant="caption"
                class="text-primary-80 grow-article-author-function">
                {{ authorFunction }}
              </Typography>
            </div>
          </div>
        </div>
      </Grid>
    </Container>
  </header>
</template>

<script setup lang="ts">
import type { GrowArticleHeaderApiModel } from '@gro-up/api/corporate'
import { Container, Grid, GrowArticleTag, Image, Typography } from '@gro-up/ui/primitives'
import { computed } from 'vue'

type GrowArticleHeaderBlockProps = GrowArticleHeaderApiModel

const props = defineProps<GrowArticleHeaderBlockProps>()

const author = computed(() => props.author)
const tags = computed(() => props.tags)

const authorFunction = computed(() => {
  if (!author.value) {
    return ''
  }

  // Prefer alternativeFunctionTitle / dataFunction when present, fall back to function
  return (
    author.value.alternativeFunctionTitle
    || (typeof author.value.function === 'string' ? author.value.function : null)
    || author.value.dataFunction
    || ''
  )
})
</script>

<style lang="scss" scoped>
.grow-article-title {
  font-size: 30px;
  line-height: 1.2;
}

.grow-article-body {
  font-size: 18px;
  line-height: 1.6;
}

.grow-article-author-name {
  font-size: 12px;
  font-weight: 700;
}

.grow-article-author-function {
  font-size: 12px;
}

@media (min-width: 1024px) {
  .grow-article-title {
    font-size: 45px;
  }

  .grow-article-body {
    font-size: 20px;
  }

  .grow-article-author-name {
    font-size: 18px;
  }

  .grow-article-author-function {
    font-size: 16px;
  }
}
</style>
